import { ReactElement, FC } from 'react';
import * as React from 'react';
import { TypographyProps, Typography, Theme, useTheme } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';

type Status = 'error' | 'important' | 'info' | 'inverse' | 'success' | 'warning';

type StyleProps = {
  status?: Status;
  isRoundBorders?: boolean;
  statusBg?: { [status: string]: string };
};

const useStyles = makeStyles<Theme, StyleProps>((theme: Theme) => {
  return {
    badge: {
      color: ({ status }) =>
        !status || status === 'warning'
          ? theme.palette.secondary.contrastText
          : theme.palette.primary.contrastText,
      padding: ({ isRoundBorders }) => (isRoundBorders ? '2px 9px' : '2px 4px'),
      borderRadius: ({ isRoundBorders }) => (isRoundBorders ? '9px' : '3px'),
      whiteSpace: 'nowrap',
      verticalAlign: 'baseline',
      backgroundColor: ({ status, statusBg }) =>
        (statusBg && status && statusBg[status]) || theme.palette.background.default,
    },
  };
});

interface Props extends TypographyProps {
  status?: Status;
  isRoundBorders?: boolean;
  title?: string;
  children?: ReactElement | React.ReactNode;
}

/**
 * @summary Плашка, доносящая какую-то информацию через
 * надпись и задний фон.
 *
 * @param status - Статус плашки. Определяет цвет заднего фона.
 * @param title - Надпись посередине плашки.
 * @param children - В качестве child-компонента можно передавать
 * надпись плашки или любой валидный ReactElement.
 * @param isRoundBorders - Флаг того, что углы закруглённые. По умолчанию
 * углы не закруглённые.
 */
const Badge: FC<Props> = ({ status, title, children, isRoundBorders, ...other }) => {
  const theme = useTheme();
  const statusBg = {
    success: theme.palette.success.main,
    info: theme.palette.info.main,
    error: theme.palette.error.main,
    important: theme.palette.error.main,
    inverse: theme.palette.grey.A100,
    warning: theme.palette.warning.main,
  };
  const classes = useStyles({ status, isRoundBorders, statusBg });

  return (
    <Typography variant="caption" classes={{ caption: classes.badge }} {...other}>
      {title || children}
    </Typography>
  );
};

export default Badge;
